import type { AuditScope } from "@/services/api/guardiansv2/types";
import { entityKindLabel, type EntityKind, type EntityRef } from "./entityDeepLink";

const OBJECT_ID_RE = /[a-fA-F0-9]{24}/;
const ENTITY_TAG_RE = /\{\{(\w+):([a-fA-F0-9]{24})(?::(\d+))?(?:\|([^}]+))?\}\}/g;

const SUPPORTED_TAG_KINDS = new Set(["artifact", "mission", "task", "interaction", "step"]);

export interface EntityTextScope {
  mission_id?: string | null;
  task_id?: string | null;
  interaction_id?: string | null;
}

export type EntityTextSegment =
  | { type: "text"; value: string }
  | { type: "entity"; ref: EntityRef; label: string | null; raw: string };

export function entityTextScopeFromAuditScope(scope: AuditScope): EntityTextScope {
  return {
    mission_id: scope.mission_id ?? null,
    task_id: scope.task_id ?? null,
    interaction_id: scope.interaction_id ?? null,
  };
}

function tagKindToEntityKind(kind: string): EntityKind | null {
  if (kind === "step") return "tool_call";
  if (kind === "artifact" || kind === "mission" || kind === "task" || kind === "interaction") {
    return kind;
  }
  return null;
}

/** Map a parsed tag into a routable {@link EntityRef}, using alert/audit scope as fallback. */
export function entityRefFromTag(
  kind: string,
  entityId: string,
  stepIndex: number | null,
  scope: EntityTextScope,
  label: string | null
): EntityRef | null {
  const entityKind = tagKindToEntityKind(kind.toLowerCase());
  if (!entityKind) return null;
  if (!OBJECT_ID_RE.test(entityId)) return null;

  const missionId = scope.mission_id ?? null;
  const taskId = scope.task_id ?? null;

  if (entityKind === "artifact") {
    return { kind: "artifact", id: entityId, version: null, scope: {}, title: label };
  }
  if (entityKind === "mission") {
    return {
      kind: "mission",
      id: entityId,
      version: null,
      scope: { mission_id: entityId },
      title: label,
    };
  }
  if (entityKind === "task") {
    return {
      kind: "task",
      id: entityId,
      version: null,
      scope: { mission_id: missionId, task_id: entityId },
      title: label,
    };
  }
  if (entityKind === "interaction") {
    return {
      kind: "interaction",
      id: entityId,
      version: null,
      scope: {
        mission_id: missionId,
        task_id: taskId,
        interaction_id: entityId,
      },
      title: label,
    };
  }
  return {
    kind: "tool_call",
    id: entityId,
    version: stepIndex,
    scope: {
      mission_id: missionId,
      task_id: taskId,
      interaction_id: entityId,
    },
    title: label,
  };
}

/** Split narrative text into plain spans and entity tag spans. */
export function parseEntityTags(text: string, scope: EntityTextScope = {}): EntityTextSegment[] {
  if (!text) return [];

  const segments: EntityTextSegment[] = [];
  let lastIndex = 0;
  ENTITY_TAG_RE.lastIndex = 0;

  for (const match of text.matchAll(ENTITY_TAG_RE)) {
    const index = match.index ?? 0;
    if (index > lastIndex) {
      segments.push({ type: "text", value: text.slice(lastIndex, index) });
    }

    const kind = match[1].toLowerCase();
    const entityId = match[2];
    const stepRaw = match[3];
    const stepIndex = stepRaw != null ? Number.parseInt(stepRaw, 10) : null;
    const labelRaw = match[4]?.trim();
    const label = labelRaw ? labelRaw : null;
    const raw = match[0];

    if (!SUPPORTED_TAG_KINDS.has(kind)) {
      segments.push({ type: "text", value: raw });
    } else {
      const ref = entityRefFromTag(kind, entityId, stepIndex, scope, label);
      if (ref) {
        segments.push({ type: "entity", ref, label, raw });
      } else {
        segments.push({ type: "text", value: raw });
      }
    }

    lastIndex = index + raw.length;
  }

  if (lastIndex < text.length) {
    segments.push({ type: "text", value: text.slice(lastIndex) });
  }

  return segments.length > 0 ? segments : [{ type: "text", value: text }];
}

export function collectEntityRefsFromText(text: string, scope: EntityTextScope = {}): EntityRef[] {
  const seen = new Set<string>();
  const refs: EntityRef[] = [];
  for (const segment of parseEntityTags(text, scope)) {
    if (segment.type !== "entity") continue;
    const key = `${segment.ref.kind}:${segment.ref.id}:${segment.ref.version ?? ""}`;
    if (seen.has(key)) continue;
    seen.add(key);
    refs.push(segment.ref);
  }
  return refs;
}

export function defaultEntityLinkLabel(kind: EntityKind): string {
  return `View ${entityKindLabel(kind)}`;
}
